import { cva } from 'class-variance-authority';
import { ChevronRight } from 'lucide-react';
import type { ComponentProps } from 'react';

import { Button, type ButtonProps } from '@vs-shop/ui/button';
import {
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@vs-shop/ui/drawer';
import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The mini-cart drawer: the panel a shopper opens from the header's cart
 * button, holding what is in the cart, what it comes to, and the two ways out
 * of it.
 *
 *   <Drawer open={open} onOpenChange={setOpen}>
 *     <CartDrawerPanel aria-describedby={undefined}>
 *       <CartDrawerDismiss aria-label="Zavřít"><XMarkIcon /></CartDrawerDismiss>
 *       <CartDrawerBar>
 *         <CartDrawerTitle>Seznam produktů</CartDrawerTitle>
 *         <CartDrawerStatus>{isWaiting ? <Loader /> : null}</CartDrawerStatus>
 *       </CartDrawerBar>
 *       <CartDrawerBody>
 *         <CartDrawerList>…</CartDrawerList>
 *         <CartDrawerFooter>…</CartDrawerFooter>
 *       </CartDrawerBody>
 *     </CartDrawerPanel>
 *   </Drawer>
 *
 * The `Drawer` root, its overlay and its focus handling are
 * `@vs-shop/ui/drawer`'s and stay there: this section is what the panel holds,
 * never a second implementation of the dialog underneath it. `CartDrawerPanel`
 * and `CartDrawerDismiss` are that primitive's `DrawerContent` and
 * `DrawerClose` wearing the section's own recipe, so the dialog contract — the
 * accessible name, the dismiss, the focus return — reaches the section unchanged.
 *
 * Which parts a drawer has, and in what order, is tenant JSX. What each part
 * paints is here, in semantic tokens, so one file renders in each tenant's
 * palette. Nothing here formats money, reads a cart, or knows a locale: copy
 * and controls arrive as children.
 *
 * The line items inside it are `./orderLineItem` — the drawer is a third
 * container for the same product line, not a fourth implementation of it.
 *
 * Server-first: every part is a plain component. The client boundary is the
 * dialog primitive's own, which is what makes a whole opened drawer a client
 * tree in practice — a tenant composes these from a client file.
 */

/* ── the panel ────────────────────────────────────────────────────────── */

/**
 * The panel itself, over the dialog primitive's own fixed frame.
 *
 * The vertical rhythm is the panel's rather than the body's: the dismiss, the
 * title bar and the body are siblings here, and the space between them belongs
 * to what holds them.
 */
const cartDrawerPanelClasses = 'tab:space-y-12 h-full space-y-6';

export type CartDrawerPanelProps = ComponentProps<typeof DrawerContent>;

/** The drawer's panel. */
export const CartDrawerPanel = ({ className, ...props }: CartDrawerPanelProps) => (
  <DrawerContent
    data-slot="cart-drawer"
    className={clsxm(cartDrawerPanelClasses, className)}
    {...props}
  />
);

/** The reserved count badge keeps the drawer row stable during a quantity write. */
export const cartQuantityDeltaVariants = cva(
  'border-primary text-foreground min-w-8 rounded border px-1 text-center text-xs',
  {
    variants: {
      visible: {
        true: '',
        false: 'invisible',
      },
    },
  }
);

export type CartQuantityDeltaProps = Omit<ComponentProps<'span'>, 'children'> & {
  readonly delta: number;
};

export const CartQuantityDelta = ({ delta, className, ...props }: CartQuantityDeltaProps) => (
  <span
    data-slot="cart-quantity-delta"
    className={clsxm(cartQuantityDeltaVariants({ visible: delta !== 0 }), className)}
    {...props}
  >
    {delta > 0 ? '+' : ''}
    {delta}
  </span>
);

/** The line keeps its size while the pseudo-element draws the active border trace. */
export const cartPendingTraceVariants = cva([
  'cart-pending-trace relative isolate',
  'before:pointer-events-none before:absolute before:inset-0 before:z-10 before:rounded-[inherit]',
  'before:border-[3px] before:border-transparent before:[background-origin:border-box]',
  'before:bg-[conic-gradient(from_var(--cart-trace-angle),transparent_0_62%,var(--color-primary)_72%_88%,transparent_98%)]',
  'before:animate-cart-trace before:opacity-0 before:content-[""]',
  'has-[[data-quantity-pending=true]]:before:opacity-100 aria-[busy=true]:before:opacity-100',
  'motion-reduce:before:bg-primary motion-reduce:before:animate-none motion-reduce:before:bg-none',
]);

/**
 * The way out of the drawer, at its top trailing corner.
 *
 * Positioned against the panel rather than placed in the title bar: it hangs
 * over whatever the panel scrolls, so it stays reachable while the item list
 * moves under it. What goes inside is the tenant's mark, and the accessible
 * name is the tenant's words.
 */
const cartDrawerDismissClasses = 'tab:top-4 tab:right-6 absolute top-2 right-2 p-2';

export type CartDrawerDismissProps = ComponentProps<typeof DrawerClose>;

/** The dismiss control. */
export const CartDrawerDismiss = ({ className, ...props }: CartDrawerDismissProps) => (
  <DrawerClose
    data-slot="cart-drawer-close"
    className={clsxm(cartDrawerDismissClasses, className)}
    {...props}
  />
);

/** The title at one end, whatever reports on the cart's state at the other. */
const cartDrawerBarClasses = 'flex w-full items-center justify-between';

export type CartDrawerBarProps = ComponentProps<'div'>;

/** The drawer's title bar. */
export const CartDrawerBar = ({ className, ...props }: CartDrawerBarProps) => (
  <div data-slot="cart-drawer-bar" className={clsxm(cartDrawerBarClasses, className)} {...props} />
);

/**
 * The drawer's title, and the dialog's accessible name.
 *
 * A heading element, which is the dialog primitive's contract rather than a
 * section part choosing one: a dialog names itself with `DrawerTitle`, and the
 * name is what assistive technology reads when the panel opens. The section's
 * no-heading rule is about content a page repeats, and this is not that.
 *
 * `className` lands on the title element; the type scale sits on the text
 * inside it, which is below the line a tenant writes.
 */
const cartDrawerTitleClasses = 'text-left';

const cartDrawerTitleTextClasses = 'font-heading tab:mt-2 tab:text-2xl/8 text-[22px] font-normal';

export type CartDrawerTitleProps = ComponentProps<typeof DrawerTitle>;

/** What the drawer is called. */
export const CartDrawerTitle = ({ className, children, ...props }: CartDrawerTitleProps) => (
  <DrawerTitle className={clsxm(cartDrawerTitleClasses, className)} {...props}>
    <span data-slot="cart-drawer-title" className={cartDrawerTitleTextClasses}>
      {children}
    </span>
  </DrawerTitle>
);

/**
 * Where the drawer reports that it is busy.
 *
 * Always rendered, even with nothing in it: it holds its place beside the
 * title, clear of the dismiss control that hangs over the panel's corner, so
 * the title does not move when the cart starts loading.
 */
const cartDrawerStatusClasses = 'pr-8';

export type CartDrawerStatusProps = ComponentProps<'div'>;

/** The place a loading indicator takes. */
export const CartDrawerStatus = ({ className, ...props }: CartDrawerStatusProps) => (
  <div
    data-slot="cart-drawer-status"
    className={clsxm(cartDrawerStatusClasses, className)}
    {...props}
  />
);

/* ── what the panel holds ─────────────────────────────────────────────── */

/**
 * The column under the title bar: what is in the cart over what it comes to.
 *
 * It is the scrolling box rather than the panel, so the title bar and the
 * dismiss stay put while the items move — and it carries no scrollbar, because
 * the panel is a surface a shopper drags rather than a document.
 */
const cartDrawerBodyClasses =
  'scrollbar-hidden tab:space-y-12 flex h-full flex-col space-y-6 overflow-y-auto';

export type CartDrawerBodyProps = ComponentProps<'div'>;

/** The drawer's body. */
export const CartDrawerBody = ({ className, ...props }: CartDrawerBodyProps) => (
  <div
    data-slot="cart-drawer-body"
    className={clsxm(cartDrawerBodyClasses, className)}
    {...props}
  />
);

/**
 * The area the items scroll in.
 *
 * `min-h-32` and `flex-grow-1`: it takes the room the footer leaves and keeps a
 * floor under it, so a cart with one item does not collapse the panel around
 * the totals.
 */
const cartDrawerListClasses = 'flex min-h-32 flex-grow-1 space-y-6 overflow-y-auto';

export type CartDrawerListProps = ComponentProps<typeof DrawerHeader>;

/** What is in the cart. */
export const CartDrawerList = ({ className, ...props }: CartDrawerListProps) => (
  <DrawerHeader
    data-slot="cart-drawer-list"
    className={clsxm(cartDrawerListClasses, className)}
    {...props}
  />
);

/** What the cart comes to, over the ways out of the drawer. */
const cartDrawerFooterClasses = 'flex flex-col space-y-8';

export type CartDrawerFooterProps = ComponentProps<typeof DrawerFooter>;

/** The drawer's footer. */
export const CartDrawerFooter = ({ className, ...props }: CartDrawerFooterProps) => (
  <DrawerFooter
    data-slot="cart-drawer-footer"
    className={clsxm(cartDrawerFooterClasses, className)}
    {...props}
  />
);

/* ── the cart before it has arrived ───────────────────────────────────── */

/**
 * What stands in for the items until the cart is known.
 *
 * Not a loading-only skeleton: the drawer shows it on its first render and
 * again while an empty cart is being refreshed, so it is the item area's
 * resting state rather than a spinner's understudy. How many stand-ins there
 * are is tenant JSX.
 *
 * `p-px` leaves the ring of a focused control inside it somewhere to paint.
 */
const cartDrawerPlaceholderClasses = 'scrollbar-hidden space-y-6 overflow-y-scroll p-px';

export type CartDrawerPlaceholderProps = ComponentProps<typeof DrawerHeader>;

/** The item area, before the items. */
export const CartDrawerPlaceholder = ({ className, ...props }: CartDrawerPlaceholderProps) => (
  <DrawerHeader
    data-slot="cart-drawer-placeholder"
    className={clsxm(cartDrawerPlaceholderClasses, className)}
    {...props}
  />
);

/**
 * The stand-in for the photograph. Its box repeats the `drawer` arm of
 * `./orderLineItem`'s media frame on purpose: what the row stands in for is that
 * frame, so the two sizes are one fact and a change to either is a change to
 * both.
 */
const placeholderMediaClasses =
  'tab:h-[100px] tab:w-[133px] bg-gray-light h-[70px] w-[85px] shrink-0 rounded-md';
const placeholderLeadClasses = 'flex w-full gap-x-5';
const placeholderCopyClasses = 'flex w-full flex-col gap-y-5';
const placeholderNameClasses = 'tab:h-6 bg-gray-light h-5 w-3/4 rounded';
const placeholderQuantityRowVariants = cva('items-center justify-between', {
  variants: {
    /** Where the row sits at this width, matching the line item it stands in for. */
    at: {
      wide: 'tab:flex hidden',
      narrow: 'tab:hidden flex',
    },
  },
});
const placeholderCounterClasses = 'bg-gray-light h-8 w-24 rounded';
const placeholderPriceClasses = 'bg-gray-light h-6 w-16 rounded';

/**
 * One stand-in item: a photograph's box, a name's bar, and the quantity row
 * that follows them — at the two places the real line item puts it, so the
 * stand-in occupies the same height the item will.
 *
 * Every box inside is anatomy below the line a tenant writes: a stand-in has no
 * content to compose, and its shape is the line item's, not a call site's.
 */

export type CartDrawerPlaceholderItemProps = ComponentProps<'div'>;

/** One stand-in line item. */
export const CartDrawerPlaceholderItem = ({
  className,
  ...props
}: CartDrawerPlaceholderItemProps) => (
  <div
    data-slot="cart-drawer-placeholder-item"
    className={optionalClassName(clsxm(className))}
    {...props}
  >
    <div data-slot="cart-drawer-placeholder-lead" className={placeholderLeadClasses}>
      <div data-slot="cart-drawer-placeholder-media" className={placeholderMediaClasses} />

      <div data-slot="cart-drawer-placeholder-copy" className={placeholderCopyClasses}>
        <div data-slot="cart-drawer-placeholder-name" className={placeholderNameClasses} />

        <div
          data-slot="cart-drawer-placeholder-quantity-row"
          className={placeholderQuantityRowVariants({ at: 'wide' })}
        >
          <div data-slot="cart-drawer-placeholder-counter" className={placeholderCounterClasses} />
          <div data-slot="cart-drawer-placeholder-price" className={placeholderPriceClasses} />
        </div>
      </div>
    </div>

    <div
      data-slot="cart-drawer-placeholder-quantity-row"
      className={placeholderQuantityRowVariants({ at: 'narrow' })}
    >
      <div data-slot="cart-drawer-placeholder-counter" className={placeholderCounterClasses} />
      <div data-slot="cart-drawer-placeholder-price" className={placeholderPriceClasses} />
    </div>
  </div>
);

/* ── the ways out ─────────────────────────────────────────────────────── */

/** The two controls at the foot of the drawer, side by side. */
const cartDrawerActionsClasses = 'flex gap-4';

export type CartDrawerActionsProps = ComponentProps<'div'>;

/** Where the drawer's controls sit. */
export const CartDrawerActions = ({ className, ...props }: CartDrawerActionsProps) => (
  <div
    data-slot="cart-drawer-actions"
    className={clsxm(cartDrawerActionsClasses, className)}
    {...props}
  />
);

/**
 * The way back: dismisses the drawer and leaves the shopper where they were.
 *
 * Its own part rather than `./checkoutActions`' way back, and the reason is the
 * width it answers to: a drawer is a fixed panel beside a page, so its controls
 * step at the panel's widths rather than the viewport's, and the two step at
 * different places. Recorded with the rest of the decision in
 * `packages/rcprofi/.docs/MODULARIZED.md`.
 */
const cartDrawerBackClasses = 'tab:text-base h-16 px-2 text-sm uppercase';

export type CartDrawerBackProps = ButtonProps;

/** The control that closes the drawer. */
export const CartDrawerBack = ({ className, ...props }: CartDrawerBackProps) => (
  <DrawerClose asChild>
    <Button
      data-slot="cart-drawer-back"
      className={clsxm(cartDrawerBackClasses, className)}
      {...props}
    />
  </DrawerClose>
);

/**
 * The way onward: dismisses the drawer and takes the shopper to the cart.
 *
 * `h-auto min-h-16` and no `whitespace-nowrap`: a label that cannot wrap sets
 * the minimum width of the row it shares with the way back, and then runs past
 * the panel's own edge. Wrapping instead costs a second line, which `h-auto`
 * makes room for while `min-h-16` keeps the single-line height it had.
 *
 * `DrawerClose` wraps the control rather than the control rendering it: the
 * primitive still dismisses through Radix's `asChild`, which cannot be the
 * child of what it dismisses. The rendered element and its handlers are the
 * same either way. It flips to `Button render={<DrawerClose … />}` — the natural
 * read order — when issue #25 migrates `DrawerClose` to Base UI's `render`; both
 * controls in this file carry the same constraint.
 */
const cartDrawerForwardClasses =
  'tab:gap-4 tab:px-10 tab:text-[22px] h-auto min-h-16 flex-1 gap-2 px-4 text-base uppercase';

/** The chevron. Decoration beside the label, so it is hidden from assistive tech. */
const cartDrawerForwardIconClasses = 'tab:size-6 text-foreground size-4 shrink-0';

export type CartDrawerForwardProps = ButtonProps;

/** The control that leaves the drawer for the cart. */
export const CartDrawerForward = ({ className, children, ...props }: CartDrawerForwardProps) => (
  <DrawerClose asChild>
    <Button
      variant="primary"
      size="2xl"
      data-slot="cart-drawer-forward"
      className={clsxm(cartDrawerForwardClasses, className)}
      {...props}
    >
      <span data-slot="cart-drawer-forward-label">{children}</span>
      <ChevronRight aria-hidden className={cartDrawerForwardIconClasses} />
    </Button>
  </DrawerClose>
);
